import React, { Fragment, memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { colors } from 'src/theme'
import { ContractorFile } from 'src/interfaces/file'
import { Button } from 'src/components/shared/Button'
import dayjs from 'dayjs'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ContractorFileItemActionsButton } from './ContractorFileItemActionsButton'
import { ContractorFilePreviewModalModal } from './ContractorFilePreviewModal'
import { useModalOpen } from 'src/hooks/useModalOpen'

interface Props {
  contractorFile: ContractorFile
  contractorId: string
  refreshContractorFiles(): void | Promise<void>
}

const ContractorFileItemComponent = ({
  contractorFile,
  contractorId,
  refreshContractorFiles,
}: Props) => {
  const {
    isOpen: isPreviewContractorFileModalOpen,
    open: openPreviewContractorFileModal,
    close: closePreviewContractorFileModal,
  } = useModalOpen()

  const creationDate = useMemo(
    () =>
      contractorFile.createdAt
        ? dayjs.unix(contractorFile.createdAt).format('MM/DD/YY • hh:mm A')
        : '',
    [contractorFile.createdAt],
  )
  const expirationDate = useMemo(
    () =>
      contractorFile.expiresAt
        ? dayjs.utc(contractorFile.expiresAt).format('MMM DD, YYYY')
        : undefined,
    [contractorFile.expiresAt],
  )
  const isExpired = useMemo(
    () =>
      contractorFile.expiresAt ? dayjs.utc(contractorFile.expiresAt).isBefore(dayjs()) : false,
    [contractorFile.expiresAt],
  )

  return (
    <Fragment>
      <div
        css={tw`flex flex-col border border-solid border-[#EDEDED] py-3 bg-white px-4 rounded-2xl mb-2`}
      >
        <div>
          <div css={tw`flex flex-row justify-between items-start`}>
            <div css={tw`flex flex-row items-start`}>
              <GSIcon iconName="gs_file_bend" />
              <Typography variant="h5" css={tw`ml-2 max-w-[200px] break-words`}>
                {contractorFile.name}
              </Typography>
            </div>
            <div css={tw`flex flex-row items-center`}>
              <Typography variant="body2" color={colors.SHADES_GREY_500}>
                {creationDate}
              </Typography>
              {contractorFile.createdBy && (
                <Tooltip
                  color="black"
                  title={
                    <Fragment>
                      <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                        Uploaded by
                      </Typography>
                      <Typography variant="body2" color={colors.SHADES_GREY_300}>
                        {contractorFile.createdBy}
                      </Typography>
                    </Fragment>
                  }
                  placement="right"
                >
                  <span css={tw`h-5 cursor-pointer ml-2`}>
                    <GSIcon iconName="gs_icon_people_circle" width={20} />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <div
            css={tw`flex flex-row border border-solid border-[#EDEDED] py-3 bg-white px-4 rounded-2xl mt-4 items-center justify-between`}
          >
            <div>
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_500}
                css={tw`max-w-[270px] break-words`}
              >
                {contractorFile?.fileName || 'file_name.pdf'}
              </Typography>
              <Typography color={isExpired ? colors.COLORS_RED_500 : undefined} variant="h6">
                {expirationDate
                  ? `${isExpired ? 'Expired' : 'Expires'} ${expirationDate}`
                  : 'No expiration set'}
              </Typography>
            </div>
            <div css={tw`flex flex-row items-center`}>
              <Button
                color="white"
                size="small"
                className={tw`w-9 h-9 mr-2`}
                leftIcon={
                  <GSIcon iconName="gs_eye_open" css={tw`self-center`} width={20} height={20} />
                }
                onClick={openPreviewContractorFileModal}
              />
              <ContractorFileItemActionsButton
                contractorId={contractorId}
                contractorFile={contractorFile}
                onUpdate={refreshContractorFiles}
              />
            </div>
          </div>
        </div>
      </div>
      <ContractorFilePreviewModalModal
        isOpen={isPreviewContractorFileModalOpen}
        contractorId={contractorId}
        contractorFile={contractorFile}
        onClose={closePreviewContractorFileModal}
      />
    </Fragment>
  )
}

export const ContractorFileItem = memo(ContractorFileItemComponent)
